import React, { useEffect, useState } from "react";
import { Modal } from "antd";
import { useTranslation } from "react-i18next";
import { updatePassword } from "./UpdatePassword.api";
import { useMutation, useQuery } from "@tanstack/react-query";
import { getUpdatePasswordToken } from "./UpdatePassword.api";
import { FiCheckCircle, FiEye, FiEyeOff, FiLock, FiShield } from "react-icons/fi";
import { AuthLayout, BrandFeature, BrandPanel } from "components/LoginForm/AuthLayout";
import { useNavigate } from "react-router-dom";
import { toastrError, toastrSuccess } from "./../../lib/toastr";
import LoadingOverlay from "react-loading-overlay-ts";

const PASSWORD_TIPS: BrandFeature[] = [
  { icon: FiLock, title: "At least 6 characters", text: "Longer passwords are harder to guess." },
  { icon: FiShield, title: "Keep it to yourself", text: "Don't share it or reuse it on other sites." },
  { icon: FiCheckCircle, title: "Then log in", text: "You'll be taken to the login page once it's updated." },
];

const eyeClass =
  "absolute right-3.5 top-1/2 z-[1] flex -translate-y-1/2 cursor-pointer text-[18px] text-ink-4 transition-colors hover:text-ink-2";

const intialState = {
  emailId: "",
  new_pass: "",
  confirm_password: "",
};

export const UpdatePassword = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [opens, setOpens] = useState(false);
  const [confirmPassError, setConfirmPassError] = useState("");

  const convertQueryParamsStringToQueryParamsObject = (queryParam: string) => {
    const search = window.location.search;
    const params = new URLSearchParams(search);
    return params.get(queryParam);
  };
  const tokenValue = convertQueryParamsStringToQueryParamsObject("token");
  const toggle = () => {
    setOpen(!open);
  };
  const toggle2 = () => {
    setOpens(!opens);
  };
  const [formValue, setFormValue] = useState(intialState);
  const { new_pass, confirm_password } = formValue;
  const mutation = useMutation(() => updatePassword(tokenValue, new_pass, confirm_password), {
    onSuccess: (response: any) => {
      if (response.success) {
        toastrSuccess(response.message);
        navigate("/login");
      } else {
        toastrError(response.message);
      }
    },
    onError: async (error: any) => {
      toastrError(error.response.data.message);
    },
  });
  const handleSubmit: React.FormEventHandler<HTMLFormElement> = (e) => {
    e.preventDefault();
    let isValid = true;

    if (formValue.new_pass.length < 6) {
      setConfirmPassError(t("form.updatepassword.characters"));
      isValid = false;
    } else {
      setConfirmPassError("");
    }
    if (formValue.confirm_password.length === 0) {
      setConfirmPassError(t("form.updatepassword.confirm"));
      isValid = false;
    } else if (formValue.confirm_password.length < 6) {
      setConfirmPassError(t("form.updatepassword.least"));
      isValid = false;
    } else if (formValue.new_pass !== formValue.confirm_password) {
      setConfirmPassError(t("form.updatepassword.match"));
      isValid = false;
    } else {
      setConfirmPassError("");
    }
    if (formValue.new_pass.length === 0 && formValue.confirm_password.length === 0) {
      setConfirmPassError(t("form.updatepassword.Fill"));
      isValid = false;
    }
    if (!isValid) {
      return;
    }
    mutation.mutate();
  };

  const checkToken = useQuery(["token"], () => getUpdatePasswordToken(tokenValue), {
    refetchOnWindowFocus: false,
  });
  useEffect(() => {
    if (checkToken && checkToken.data) {
      if (!checkToken.data.success) {
        Modal.error({
          title: "Error",
          content: "Token is expired",
          onOk() {
            navigate("/login");
          },
        });
      }
    }
  }, [checkToken]);

  const onInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    const trimmedValue = value.trim();
    setFormValue({ ...formValue, [name]: trimmedValue });

    setConfirmPassError("");
  };

  return (
    <LoadingOverlay
      styles={{
        overlay: (base) => ({
          ...base,
          position: "fixed",
        }),
      }}
      active={checkToken.isLoading || mutation.isLoading}
      spinner
      text={t("claim.card.Loading")}
    >
      <AuthLayout
        aside={
          <BrandPanel
            eyebrow="Account security"
            title="Choose a new password"
            description="Pick something you haven't used elsewhere. You'll use it to log in from now on."
            features={PASSWORD_TIPS}
          />
        }
      >
        <section className="zs-card w-full max-w-[460px] animate-fadeInUp">
          <form className="px-6 pt-7 pb-7 sm:px-9 sm:pt-9 sm:pb-9" onSubmit={handleSubmit}>
            <span className="mb-5 inline-flex h-11 w-11 items-center justify-center rounded-lg bg-primary-soft text-primary-text">
              <FiLock className="h-5 w-5" aria-hidden="true" />
            </span>
            <h2
              data-testid="heading"
              className="font-display text-2xl font-bold leading-tight tracking-tight text-ink"
            >
              {t("form.updatepassword.UpdateYourPassword")}
            </h2>
            <p className="mt-2 text-sm leading-relaxed text-ink-3">
              Enter your new password twice to confirm it.
            </p>

            <div data-testid="new_password" className="mt-7">
              <label
                htmlFor="new_password"
                aria-label="new_password"
                className="zs-label zs-label-required"
              >
                {t("form.updatepassword.NewPassword")}
              </label>
              <div className="relative">
                <div className={eyeClass}>
                  {new_pass.length > 0 && (
                    <>
                      {opens === false ? (
                        <FiEyeOff onClick={toggle2} />
                      ) : (
                        <FiEye onClick={toggle2} />
                      )}
                    </>
                  )}
                </div>
                <input
                  type={opens === true ? "text" : "password"}
                  name="new_pass"
                  id="new_pass"
                  className="zs-input h-11 pr-11"
                  value={new_pass}
                  onChange={onInputChange}
                  placeholder={t("form.login.password")}
                />
              </div>
            </div>
            <div data-testid="password" className="mt-5">
              <label
                htmlFor="password"
                aria-label="password"
                className="zs-label zs-label-required"
              >
                {t("form.updatepassword.ConfirmPassword")}
              </label>
              <div className="relative">
                <div className={eyeClass}>
                  {confirm_password.length > 0 && (
                    <>
                      {open === false ? (
                        <FiEyeOff onClick={toggle} />
                      ) : (
                        <FiEye onClick={toggle} />
                      )}
                    </>
                  )}
                </div>
                <input
                  type={open === true ? "text" : "password"}
                  value={confirm_password}
                  name="confirm_password"
                  id="confirm_password"
                  aria-invalid={confirmPassError ? true : undefined}
                  className="zs-input h-11 pr-11"
                  onChange={onInputChange}
                  placeholder={t("form.login.password")}
                />
              </div>
              {confirmPassError && (
                <p className="zs-error">
                  {confirmPassError}
                </p>
              )}
            </div>
            <div data-testid="button" className="mt-7">
              <button
                type="submit"
                className="zs-btn zs-btn-primary zs-btn-lg zs-btn-block"
              >
                {t("form.updatepassword.Submit")}
              </button>
            </div>
          </form>
        </section>
      </AuthLayout>
    </LoadingOverlay>
  );
};
